import type {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from "react";
import type { Region } from "@gooner/shared";
import { GRID } from "../lib/regionGeometry";
import type { HandleId } from "../lib/resizeHit";
import { cx } from "../ui/primitives";
import { RegionTileLabels } from "./RegionTileLabels";

/**
 * What a view LOOKS like — the aspect-correct surface and its region boxes — with no
 * pointer handling, no selection and no draw state.
 *
 * This is a straight extraction out of RegionCanvas, which now imports it. The point is
 * that there is exactly one definition: a view card and the editor's canvas draw the
 * same boxes from the same file, so they cannot drift into disagreeing about what a view
 * looks like. RegionCanvas = this surface + interaction.
 */

// The handle names live with the touch hit-test (lib/resizeHit), which is pure and has
// no business importing a component; re-exported so this stays the one import site for
// everything about handles that a component needs.
export type { HandleId };

const HANDLES: ReadonlyArray<{
  id: HandleId;
  x: number;
  y: number;
  cursor: string;
}> = [
  { id: "nw", x: 0, y: 0, cursor: "nwse-resize" },
  { id: "n", x: 0.5, y: 0, cursor: "ns-resize" },
  { id: "ne", x: 1, y: 0, cursor: "nesw-resize" },
  { id: "e", x: 1, y: 0.5, cursor: "ew-resize" },
  { id: "se", x: 1, y: 1, cursor: "nwse-resize" },
  { id: "s", x: 0.5, y: 1, cursor: "ns-resize" },
  { id: "sw", x: 0, y: 1, cursor: "nesw-resize" },
  { id: "w", x: 0, y: 0.5, cursor: "ew-resize" },
];

/** What a region is currently playing, shown inside its box. */
export interface RegionMeta {
  feed: string;
  /** Pacing caption (a clip-length band, e.g. "shorts · minis"). Absent when there
   *  is nothing to say — an unfiltered region gets no pill, not an "any length" one. */
  timer?: string | null;
  /** No feed picked yet — the box is washed in the accent to call for one. */
  unset?: boolean;
}

/** Fills its column's width and takes whatever height its aspect implies. */
export const SURFACE_CLASS =
  "relative w-full select-none overflow-hidden rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)]";

/**
 * The surface's box style. `grid` paints the editor's 10% guide grid; a view card passes
 * it false, because the spec's card is "the editor's canvas with everything interactive
 * removed: … no hint text, no grid".
 */
export function surfaceStyle(
  aspect: { w: number; h: number },
  opts: {
    grid?: boolean;
    maxHeight?: string;
    cursor?: string;
  } = {},
): CSSProperties {
  const { grid = false, maxHeight, cursor } = opts;
  return {
    aspectRatio: `${aspect.w} / ${aspect.h}`,
    ...(maxHeight ? { maxHeight } : {}),
    ...(grid
      ? {
          backgroundImage:
            "linear-gradient(var(--color-edge) 1px, transparent 1px), linear-gradient(90deg, var(--color-edge) 1px, transparent 1px)",
          // The SAME divisor the drags snap to. These were 10% while nothing snapped at
          // all, which was merely decorative; now that edges land on gridlines, a drawn
          // grid that disagreed with the real one would be worse than no grid — you would
          // aim at a line and land somewhere else.
          backgroundSize: `${100 / GRID}% ${100 / GRID}%, ${100 / GRID}% ${100 / GRID}%`,
        }
      : {}),
    touchAction: "none",
    ...(cursor ? { cursor } : {}),
  };
}

export function RegionBox({
  region,
  index,
  selected,
  meta,
  preview,
  syncTint,
  interactive = true,
  labels = true,
  action,
  onPointerDown,
  onResizeStart,
}: {
  region: Region;
  index: number;
  /** The selection: gets resize handles and the accent ring. */
  selected: boolean;
  meta: RegionMeta | null;
  preview: ReactNode;
  /** Sync-group colour for the name badge: every region in one sync group wears the
   *  same tint, so linked tiles read as a set at a glance. Absent = default badge. */
  syncTint?: string | null;
  /** false drops the hover ring, the move cursor and the pointer handlers. */
  interactive?: boolean;
  /** false hides the name/feed/timer pills — a card-sized surface has no room. */
  labels?: boolean;
  /** An interactive control in the tile's top-right corner (the editor's link button).
   *  Rendered above the preview; the control itself must stop pointer propagation or
   *  a click on it starts a move. */
  action?: ReactNode;
  onPointerDown?: (e: ReactPointerEvent) => void;
  onResizeStart?: (e: ReactPointerEvent, handle: HandleId) => void;
}) {
  return (
    <div
      onPointerDown={interactive ? onPointerDown : undefined}
      className={cx(
        "absolute rounded-md border transition-shadow",
        selected
          ? "z-20 border-[var(--color-accent)] bg-[var(--color-accent)]/15 shadow-[0_0_0_1px_var(--color-accent)]"
          : cx(
              "z-10 border-[var(--color-edge)] bg-[var(--color-panel-2)]/60",
              interactive ? "hover:border-[var(--color-accent)]/60" : "",
            ),
      )}
      style={{
        left: `${region.x * 100}%`,
        top: `${region.y * 100}%`,
        width: `${region.w * 100}%`,
        height: `${region.h * 100}%`,
        ...(interactive ? { cursor: "move" } : {}),
      }}
    >
      {/* The clip lives on THIS wrapper and not on the box, so the handles below — which
          straddle the box edge — are not cut in half. They were: `overflow-hidden` on the
          box clipped the outside half of every handle for paint AND for hit-testing, which
          made a corner a 5px target. Footage, pills and the corner control still need the
          clip, so they get it here. */}
      <div className="absolute inset-0 flex items-start justify-between overflow-hidden rounded-[inherit]">
        {preview ? (
          <div className="pointer-events-none absolute inset-0 isolate bg-black">
            {/* A region with a feed plays it as-is. One still waiting on a feed is
                desaturated and washed in the accent, so an unconfigured tile is
                obvious at a glance. Grayscale must sit on its own element:
                `filter` applies to every descendant, so nesting the tint inside
                would desaturate that too and cancel the wash. */}
            <div
              className={cx("absolute inset-0", meta?.unset ? "grayscale" : "")}
            >
              {preview}
            </div>
            {meta?.unset ? (
              <div
                className={cx(
                  "absolute inset-0 mix-blend-multiply",
                  selected
                    ? "bg-[var(--color-accent)]/50"
                    : "bg-[var(--color-accent)]/75",
                )}
              />
            ) : null}
            {/* Keeps the name/feed/timer pills legible over bright footage.
                Anything in the selection stays bright; the rest dims back. */}
            <div
              className={cx(
                "absolute inset-0",
                selected ? "bg-black/5" : "bg-black/25",
              )}
            />
          </div>
        ) : null}

        {action ? <div className="absolute right-1 top-1 z-30">{action}</div> : null}

        {labels ? (
          <RegionTileLabels
            name={region.name}
            index={index}
            meta={meta}
            selected={selected}
            syncTint={syncTint}
          />
        ) : null}
      </div>

      {/* Each handle is a transparent hit square with the visible grip centred in it.
          The square is the mouse's target — 16px, 24px for a finger — and the grip is
          what you see: 10px, 14px for a finger, following the house rule that coarse
          grows and nothing shrinks (see primitives.regionChromeButton). On touch the
          canvas has usually already taken the press by the time it would reach a handle
          (RegionCanvas grabs an edge from anywhere within a finger's width of it), so
          these are the mouse path and the picture of where the edges are. */}
      {selected && onResizeStart
        ? HANDLES.map((h) => (
            <span
              key={h.id}
              onPointerDown={(e) => onResizeStart(e, h.id)}
              className="absolute z-30 flex h-4 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center pointer-coarse:h-6 pointer-coarse:w-6"
              style={{
                left: `${h.x * 100}%`,
                top: `${h.y * 100}%`,
                cursor: h.cursor,
              }}
            >
              <span className="block h-2.5 w-2.5 rounded-[2px] border border-white/80 bg-[var(--color-accent)] pointer-coarse:h-3.5 pointer-coarse:w-3.5" />
            </span>
          ))
        : null}
    </div>
  );
}

/**
 * A view, drawn and nothing else: no draw, move or resize, no selection, no handles, no
 * hint text, no grid. This is what a view card renders — you recognise a view by looking
 * at it, so the card shows the thing rather than describing it.
 */
export function ViewSurface({
  view,
  renderPreview,
  describeRegion,
  labels = false,
  className,
  maxHeight,
}: {
  view: { aspect: { w: number; h: number }; regions: Region[] };
  renderPreview?: (region: Region) => ReactNode;
  describeRegion?: (region: Region) => RegionMeta | null;
  labels?: boolean;
  className?: string;
  maxHeight?: string;
}) {
  return (
    <div
      className={cx(SURFACE_CLASS, className)}
      style={surfaceStyle(view.aspect, { maxHeight })}
    >
      {view.regions.map((region, i) => (
        <RegionBox
          key={region.id}
          region={region}
          index={i}
          selected={false}
          meta={describeRegion?.(region) ?? null}
          preview={renderPreview?.(region) ?? null}
          interactive={false}
          labels={labels}
        />
      ))}
    </div>
  );
}
